import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { submitFormData } from '../../../../pages/api';

const CellFixingType = ({ globalFields, formData, onFormDataChange }) => {
    const [localFormData, setLocalFormData] = useState(formData);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const baseURL = process.env.REACT_APP_API_BASE_URL;

    // Sync local state with props
    useEffect(() => {
        setLocalFormData(formData);
    }, [formData]);

    // Prefill data from sessionStorage
    useEffect(() => {
        const isFormDataEmpty = Object.values(formData).every(
            (value) => value === '' || value === null || value === undefined
        );

        if (isFormDataEmpty) {
            const inspectionData = sessionStorage.getItem('inspectionData');
            if (inspectionData) {
                try {
                    const parsedData = JSON.parse(inspectionData);
                    const cellFixingTypeData = parsedData?.BOM?.cellFixingType;

                    if (cellFixingTypeData) {
                        setLocalFormData((prevData) => ({
                            ...prevData,
                            make: cellFixingTypeData.make || prevData.make,
                            type: cellFixingTypeData.type || prevData.type,
                            size: cellFixingTypeData.size || prevData.size,
                            lotNo: cellFixingTypeData.lotNo || prevData.lotNo,
                            remarks: cellFixingTypeData.remarks || prevData.remarks,
                        }));
                    }
                } catch (error) {
                    console.error('Error parsing inspectionData:', error);
                }
            }
        }
    }, []);

    // Fetch uploaded images
    useEffect(() => {
        const fetchUploadedImages = async () => {
            try {
                const projectId = localStorage.getItem('selectedProject');
                const inspectionDate = sessionStorage.getItem('inspectionDate');
                const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');
                const inspectionShift = sessionStorage.getItem('inspectionShift');

                const response = await axios.get(`${baseURL}get_uploaded_inspection_files`, {
                    params: {
                        projectId,
                        inspectionSupplier,
                        inspectionDate,
                        inspectionShift,
                        formName: 'CellFixingType',
                        inputFieldId: 'Upload PhotoCellFixingType'
                    }
                });
                setUploadedImages(response.data.files || []);
            } catch (error) {
                console.error('Error fetching uploaded images:', error);
            }
        };

        fetchUploadedImages();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedData = { ...localFormData, [name]: value };
        setLocalFormData(updatedData);
        onFormDataChange(updatedData);
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const updatedData = { ...localFormData, uploadPhoto: files };
        setLocalFormData(updatedData);
        onFormDataChange(updatedData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');
    

        try {
            // Upload files if any
            if (localFormData.uploadPhoto && Array.isArray(localFormData.uploadPhoto) && localFormData.uploadPhoto.length > 0) {
                const base64Files = await Promise.all(
                    localFormData.uploadPhoto.map(async (file) => ({
                        inputFieldId: 'Upload PhotoCellFixingType',
                        file: await convertToBase64(file),
                        filename: file.name,
                    }))
                );

                await axios.post(`${baseURL}upload_inspection_files`, {
                    projectId,
                    inspectionSupplier,
                    inspectionDate,
                    inspectionShift,
                    inspectorName,
                    formName: 'CellFixingType',
                    files: base64Files
                });

                // Refresh uploaded images
                const response = await axios.get(`${baseURL}get_uploaded_inspection_files`, {
                    params: {
                        projectId,
                        inspectionSupplier,
                        inspectionDate,
                        inspectionShift,
                        formName: 'CellFixingType',
                        inputFieldId: 'Upload PhotoCellFixingType'
                    }
                });
                setUploadedImages(response.data.files || []);
            }

            // Submit form data
            const payload = {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                inspectorName,
                status: 'completed',
                BOM: {
                    cellFixingType: {
                        make: localFormData.make,
                        type: localFormData.type,
                        size: localFormData.size,
                        lotNo: localFormData.lotNo,
                        remarks: localFormData.remarks,
                    },
                },
            };

            const response = await submitFormData(payload);
            alert('Form submitted successfully');
        } catch (error) {
            console.error('Error submitting form or uploading files:', error);
            alert('Failed to submit form or upload files');
        }
    };

    const openModal = (url) => {
        setSelectedImageUrl(url);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedImageUrl('');
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-lg space-y-4">
            <h2 className="text-2xl font-semibold mb-4">Cell Fixing Type</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Make */}
                <div>
                    <label className="block font-semibold mb-1">Make</label>
                    <input
                        type="text"
                        name="make"
                        value={localFormData.make || ''}
                        onChange={handleChange}
                        placeholder="Make"
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                </div>
                {/* Type */}
                <div>
                    <label className="block font-semibold mb-1">Type</label>
                    <input
                        type="text"
                        name="type"
                        value={localFormData.type || ''}
                        onChange={handleChange}
                        placeholder="Type"
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                </div>
                {/* Size */}
                <div>
                    <label className="block font-semibold mb-1">Size</label>
                    <input
                        type="text"
                        name="size"
                        value={localFormData.size || ''}
                        onChange={handleChange}
                        placeholder="Size"
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                </div>
                {/* Lot No. */}
                <div>
                    <label className="block font-semibold mb-1">Lot No. (Barcode Scan)</label>
                    <input
                        type="text"
                        name="lotNo"
                        value={localFormData.lotNo || ''}
                        onChange={handleChange}
                        placeholder="Scan the barcode"
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                </div>
                {/* Upload Photo */}
                <div>
                    <label className="block font-semibold mb-1">Upload Photo</label>
                    <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {localFormData.uploadPhoto && Array.isArray(localFormData.uploadPhoto) && (
                        <div className="text-gray-600 mt-1">
                            {localFormData.uploadPhoto.map((file, index) => (
                                <p key={index}>{file.name}</p>
                            ))}
                        </div>
                    )}
                </div>
                {/* Display Uploaded Images */}
                <div className="col-span-full">
                    <label className="block font-semibold mb-1">Uploaded Images</label>
                    {uploadedImages.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {uploadedImages.map((image, index) => (
                                <div key={index} className="relative">
                                    <img
                                        src={image.url}
                                        alt={image.filename || `Uploaded ${index}`}
                                        className="w-full h-32 object-cover rounded cursor-pointer"
                                        onClick={() => openModal(image.url)}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500">No images uploaded.</p>
                    )}
                </div>
                {/* Remarks */}
                <div className="col-span-full">
                    <label className="block font-semibold mb-1">Remarks (In case of non-conformance)</label>
                    <textarea
                        name="remarks"
                        value={localFormData.remarks || ''}
                        onChange={handleChange}
                        placeholder="Remarks"
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                </div>
            </div>
            {/* Modal for Full Image View */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="relative bg-white p-4 rounded-lg max-w-4xl max-h-[90vh]">
                        <button
                            className="absolute top-2 right-2 text-black text-2xl font-bold"
                            onClick={closeModal}
                        >
                            ×
                        </button>
                        <img
                            src={selectedImageUrl}
                            alt="Full view"
                            className="max-w-full max-h-[80vh] object-contain"
                        />
                    </div>
                </div>
            )}
            {/* Submit Button */}
            <button
                type="submit"
                className="mt-4 bg-[#424752] text-white px-4 py-2 rounded hover:bg-gray-500 transition-colors"
            >
                Submit
            </button>
        </form>
    );
};

export default CellFixingType;